import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ROUTES } from '@shared/constants'
import { Icon } from '@/components/ui/Icon'
import { NotificationsMenu } from './NotificationsMenu'
import { CopilotPanel } from './CopilotPanel'

const ROUTE_LABELS: Record<string, string> = {
  [ROUTES.DASHBOARD]:     'Dashboard',
  [ROUTES.LIVE_WALL]:     'Live Wall',
  [ROUTES.CONVERSATIONS]: 'Conversations',
  [ROUTES.ALERTS]:        'Alerts & Escalations',
  [ROUTES.LOGS]:          'Logs',
  [ROUTES.KNOWLEDGE_BASE]:'Knowledge Base',
  [ROUTES.FLOWS]:         'Flows',
  [ROUTES.PROMPTS]:       'Prompts',
  [ROUTES.RETRIEVAL]:     'Retrieval',
  [ROUTES.CRM]:           'Customers & Roles',
  [ROUTES.LLM_CONFIG]:    'LLM Config',
  [ROUTES.VOICE_STYLE]:   'Voice & Style',
  [ROUTES.APPEARANCE]:    'Appearance',
}

/**
 * Where "Open chat" points. This was hardcoded to http://localhost:5174, so on
 * every deployed environment the button sent the operator to their own machine.
 * VITE_CUSTOMER_APP_URL is set per environment; the localhost default only
 * applies to a dev build, where it is correct.
 */
const CUSTOMER_APP_URL =
  (import.meta.env.VITE_CUSTOMER_APP_URL as string | undefined)?.trim() ||
  'http://localhost:5174'

interface TopBarProps {
  onOpenCommandPalette: () => void
}

export function TopBar({ onOpenCommandPalette }: TopBarProps) {
  const { pathname } = useLocation()
  const pageLabel = ROUTE_LABELS[pathname] ?? 'Dashboard'
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [copilotOpen, setCopilotOpen] = useState(false)

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        onOpenCommandPalette()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onOpenCommandPalette])

  return (
    <div style={{
      minHeight: 76,
      padding: '12px 28px',
      display: 'grid',
      gridTemplateColumns: 'minmax(210px, 1fr) auto minmax(360px, 1fr)',
      alignItems: 'center',
      borderBottom: '1px solid var(--line)',
      background: 'rgba(255,255,255,.92)',
      position: 'sticky',
      top: 0,
      zIndex: 20,
      flexShrink: 0,
    }}>
      {/* Left: breadcrumbs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <span style={{ color: 'var(--ink)', fontWeight: 600 }}>CX Admin</span>
          <Icon name="chevron" size={12} stroke={2} style={{ color: 'var(--ink-4)' }} />
          <span style={{ fontWeight: 500, color: 'var(--ink)' }}>{pageLabel}</span>
        </div>
      </div>

      <span className="chip" style={{
        background: 'white',
        color: 'var(--ink-2)',
        borderColor: 'var(--line)',
        padding: '9px 14px',
        fontSize: 12.5,
        boxShadow: 'var(--sh-1)',
      }}>
        <span style={{ width: 8, height: 8, borderRadius: 99, background: 'var(--ok)', display: 'inline-block' }} />
        Live in production
      </span>

      {/* Right: actions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
        <a
          className="btn"
          href={CUSTOMER_APP_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon name="prompt" size={15} /> Open chat
        </a>
        <button
          className="btn ghost icon"
          onClick={onOpenCommandPalette}
          data-tip="⌘K"
          aria-label="Search"
        >
          <Icon name="search" size={14} />
        </button>

        <NotificationsMenu open={notificationsOpen} onOpenChange={setNotificationsOpen} />

        <button
          className="btn brand"
          onClick={() => setCopilotOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={copilotOpen}
          style={{ padding: '10px 16px', borderRadius: 10 }}
        >
          <Icon name="sparkle" size={14} /> Ask Copilot
        </button>
      </div>

      <CopilotPanel open={copilotOpen} onClose={() => setCopilotOpen(false)} />
    </div>
  )
}
